/* eslint-disable react/jsx-props-no-spreading */
import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Fade from "@mui/material/Fade";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Slide from "@mui/material/Slide";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";

import { get, post } from "lib/api";
import { getAiContext } from "lib/aiContext";

const isString = (v) => typeof v === "string";

const formatError = (err) => {
  const msg =
    err?.response?.data?.error?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    "Request failed";
  return isString(msg) ? msg : "Request failed";
};

const renderInlineBold = (text) => {
  const s = String(text || "");
  const parts = s.split(/\*\*(.*?)\*\*/g);
  return parts.map((p, idx) => (idx % 2 === 1 ? <strong key={idx}>{p}</strong> : <span key={idx}>{p}</span>));
};

const renderMarkdownLite = (text) => {
  const s = String(text || "");
  const lines = s.split("\n");
  const bullets = [];
  const blocks = [];

  const flushBullets = () => {
    if (!bullets.length) return;
    blocks.push(
      <List dense sx={{ pt: 0 }} key={`bullets-${blocks.length}`}>
        {bullets.splice(0).map((li, i) => (
          <ListItemText
            key={i}
            primaryTypographyProps={{ variant: "body2" }}
            primary={
              <span>
                • {renderInlineBold(li)}
              </span>
            }
          />
        ))}
      </List>
    );
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushBullets();
      blocks.push(<div key={`sp-${blocks.length}`} style={{ height: 8 }} />);
      return;
    }
    if (trimmed.startsWith("- ")) {
      bullets.push(trimmed.slice(2));
      return;
    }
    flushBullets();
    blocks.push(
      <Typography variant="body2" key={`p-${blocks.length}`} sx={{ whiteSpace: "pre-wrap" }}>
        {renderInlineBold(trimmed)}
      </Typography>
    );
  });
  flushBullets();
  return <Stack spacing={0.75}>{blocks}</Stack>;
};

const toMobileRouteIfSupported = (route) => {
  if (!route) return null;
  const r = String(route);
  if (r.startsWith("/m/") || r === "/m") return r;
  const supportedPrefixes = [
    "/dashboard",
    "/auth-dashboard",
    "/pipeline",
    "/work-orders",
    "/inventory",
    "/internal/valve-bodies",
    "/media/gallery",
    "/sales",
    "/core-returns",
    "/purchase-orders",
    "/warehouse/receiving",
    "/projections",
    "/invoices",
    "/payments",
    "/settings",
    "/catalog",
    "/valve-bodies",
    "/authentication/sign-in",
    "/restricted",
    "/sign-out",
  ];
  if (supportedPrefixes.some((p) => r === p || r.startsWith(`${p}/`))) return `/m${r}`;
  return r;
};

export default function MobileSearchOverlay({ open, onClose, onNavigate }) {
  const [mode, setMode] = useState("search"); // search | ai
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);

  const [chatMessages, setChatMessages] = useState([]);
  const [replyValue, setReplyValue] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [lastResponseId, setLastResponseId] = useState(null);

  const debounceRef = useRef(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    if (!open) return;
    setError("");
    // Do not auto-focus on open. Also blur any currently-focused element so
    // mobile Safari/Chrome won't keep a prior field focused / pop the keyboard.
    try {
      if (typeof document !== "undefined") document.activeElement?.blur?.();
    } catch (_) {}
    return undefined;
  }, [open]);

  // Reset transient state when closing
  useEffect(() => {
    if (open) return;
    setLoading(false);
    setError("");
    setResults([]);
    setReplyValue("");
    setSendingReply(false);
  }, [open]);

  const grouped = useMemo(() => {
    const list = Array.isArray(results) ? results : [];
    const map = new Map();
    list.forEach((r) => {
      const g = r?.group || "Results";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(r);
    });
    return [...map.entries()];
  }, [results]);

  useEffect(() => {
    if (!open) return;
    if (mode !== "search") return;

    const q = inputValue.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!q || q.length < 2) {
      setLoading(false);
      setResults([]);
      setError("");
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const seq = (requestSeq.current += 1);
      setLoading(true);
      setError("");
      try {
        const res = await get("/api/global-search", { q, limitPerType: 10 });
        if (seq !== requestSeq.current) return;
        const list = Array.isArray(res?.results) ? res.results : [];
        const normalized = list
          .filter((r) => r && r.label)
          .map((r) => ({
            group: r.group || "Results",
            label: r.label,
            subtitle: r.subtitle || "",
            route: r.route || "",
            type: r.type,
            id: r.documentId || r.id,
            key: `${r.type}:${r.documentId || r.id || r.label}`,
          }));
        setResults(normalized);
      } catch (e) {
        if (seq !== requestSeq.current) return;
        setResults([]);
        setError(formatError(e));
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, 220);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, mode, inputValue]);

  const startAsk = async (question, previousResponseId) => {
    const q = String(question || "").trim();
    if (!q) return;
    setMode("ai");
    setLoading(true);
    setError("");
    try {
      const res = await post("/api/assistant/ask", { question: q, previousResponseId, context: getAiContext() });
      setLastResponseId(res?.responseId || previousResponseId || null);
      setChatMessages((prev) => [...prev, { role: "assistant", content: res?.answer || "" }]);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const q = inputValue.trim();
    if (!q) return;
    if (mode === "ai") {
      setChatMessages((prev) => [...prev, { role: "user", content: q }]);
      setInputValue("");
      await startAsk(q, lastResponseId);
    }
  };

  const sendReply = async () => {
    const msg = replyValue.trim();
    if (!msg || sendingReply) return;
    setSendingReply(true);
    setChatMessages((prev) => [...prev, { role: "user", content: msg }]);
    setReplyValue("");
    try {
      const res = await post("/api/assistant/ask", { question: msg, previousResponseId: lastResponseId, context: getAiContext() });
      setLastResponseId(res?.responseId || lastResponseId || null);
      setChatMessages((prev) => [...prev, { role: "assistant", content: res?.answer || "" }]);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSendingReply(false);
    }
  };

  const navigateTo = (route) => {
    const target = toMobileRouteIfSupported(route);
    if (!target) return;
    onClose();
    onNavigate(target);
  };

  const fieldSx = {
    "& .MuiInputBase-root": {
      // Outlook-ish: subtle elevation + small radius
      bgcolor: "rgba(255,255,255,0.08)",
      borderRadius: "12px",
      color: "#ffffff",
    },
    "& .MuiInputBase-input": {
      color: "#ffffff",
      // Prevent iOS Safari from zooming when focusing inputs.
      fontSize: "16px",
      // Keep caret visible for usability, but avoid “focus glow”.
      caretColor: "#ffffff",
      outline: "none",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#ffffff",
      opacity: 1,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.14)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.18)",
    },
    // Keep focus subtle (no “blue glow”)
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      // Do not visually change when focused.
      borderColor: "rgba(255,255,255,0.18)",
      borderWidth: 1,
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: "none",
    },
    // Also prevent focus-visible rings
    "& .MuiOutlinedInput-root.Mui-focusVisible .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.18)",
    },
    // iOS/Safari focus artifacts
    "& input:focus": {
      outline: "none",
    },
  };

  const typeAccent = (type) => {
    const t = String(type || "");
    if (t === "inventory-item") return { icon: "inventory_2", color: "rgba(56,189,248,0.95)" };
    if (t === "work-order") return { icon: "build", color: "rgba(251,191,36,0.95)" };
    if (t === "purchase-order") return { icon: "shopping_cart", color: "rgba(34,197,94,0.95)" };
    if (t === "sales-order") return { icon: "receipt_long", color: "rgba(168,85,247,0.95)" };
    if (t === "build-sheet") return { icon: "article", color: "rgba(244,114,182,0.95)" };
    if (t === "valve-body") return { icon: "qr_code_2", color: "rgba(99,102,241,0.95)" };
    if (t === "customer") return { icon: "people", color: "rgba(59,130,246,0.95)" };
    if (t === "vendor") return { icon: "store", color: "rgba(245,158,11,0.95)" };
    if (t === "payment") return { icon: "payments", color: "rgba(16,185,129,0.95)" };
    return { icon: "search", color: "rgba(255,255,255,0.75)" };
  };

  return (
    <Fade in={open} unmountOnExit>
      <Backdrop
        open={open}
        sx={{
          zIndex: 3200,
          color: "#ffffff",
          alignItems: "stretch",
          justifyContent: "stretch",
          // Full black overlay
          backgroundColor: "#000000",
        }}
        onClick={onClose}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: "100%",
            height: "100dvh",
            // Full-bleed black layer should NOT look rounded.
            borderRadius: 0,
            display: "flex",
            flexDirection: "column",
            minHeight: 0, // critical for nested scroll containers in flex layouts
            // Force every text/icon in the overlay to pure white.
            color: "#ffffff",
            "& .MuiTypography-root": { color: "#ffffff" },
            "& .MuiIcon-root": { color: "#ffffff" },
            "& .MuiSvgIcon-root": { color: "#ffffff" },
            "& .MuiListItemText-primary": { color: "#ffffff" },
            "& .MuiListItemText-secondary": { color: "#ffffff" },
            "& .MuiInputAdornment-root": { color: "#ffffff" },
            "& .MuiInputBase-input::placeholder": { color: "#ffffff", opacity: 1 },
            // Ensure disabled text/icons remain visible (still white).
            "& .Mui-disabled": { color: "#ffffff !important", WebkitTextFillColor: "#ffffff !important", opacity: 0.6 },
          }}
        >
          {/* Top sheet (slides down) */}
          <Slide in={open} direction="down" mountOnEnter unmountOnExit>
            <Box sx={{ pt: "env(safe-area-inset-top)", px: 1.25, pb: 1.0 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "14px",
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.10)",
                  bgcolor: "rgba(12,14,18,0.92)",
                  color: "#ffffff",
                }}
              >
                <Box sx={{ p: 1 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.75}>
                    <Stack spacing={0}>
                      <Typography variant="subtitle2" fontWeight={900} sx={{ color: "#ffffff" }}>
                        Search
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 1, color: "#ffffff" }}>
                        Global search + AI assistant
                      </Typography>
                    </Stack>
                    <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.9)" }}>
                      <Icon fontSize="small">close</Icon>
                    </IconButton>
                  </Stack>

                  <Stack spacing={0.75} sx={{ mt: 1 }}>
                    <ToggleButtonGroup
                      value={mode}
                      exclusive
                      onChange={(e, v) => {
                        if (!v) return;
                        setError("");
                        setMode(v);
                        if (v === "ai" && chatMessages.length === 0) {
                          setChatMessages([{ role: "assistant", content: "Ask me anything about VB data." }]);
                        }
                      }}
                      size="small"
                      sx={{
                        "& .MuiToggleButton-root": {
                          color: "#ffffff",
                          borderColor: "rgba(255,255,255,0.14)",
                          textTransform: "none",
                          fontWeight: 900,
                          borderRadius: "10px",
                          paddingLeft: "12px",
                          paddingRight: "12px",
                        },
                        "& .MuiToggleButton-root.Mui-selected": {
                          bgcolor: "rgba(255,255,255,0.12)",
                          color: "#ffffff",
                        },
                      }}
                    >
                      <ToggleButton value="search">Search</ToggleButton>
                      <ToggleButton value="ai">AI</ToggleButton>
                    </ToggleButtonGroup>

                    <TextField
                      fullWidth
                      placeholder={mode === "ai" ? 'Ask: "What did we sell this month?"' : "Search: WO-123, INV-..., vendor name…"}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (mode === "ai") handleSubmit();
                        }
                        if (e.key === "Escape") onClose();
                      }}
                      sx={fieldSx}
                      inputProps={{
                        autoCorrect: "off",
                        autoCapitalize: "none",
                        spellCheck: "false",
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon sx={{ color: "#ffffff" }}>{mode === "ai" ? "smart_toy" : "search"}</Icon>
                          </InputAdornment>
                        ),
                        endAdornment:
                          mode === "ai" ? (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="Send"
                                onClick={handleSubmit}
                                size="small"
                                sx={{ color: "#ffffff" }}
                                disabled={!inputValue.trim() || loading}
                              >
                                <Icon fontSize="small">send</Icon>
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                      }}
                    />

                    {loading ? <LinearProgress sx={{ opacity: 0.7 }} /> : null}
                    {error ? (
                      <Typography variant="caption" sx={{ color: "#ffffff" }}>
                        {error}
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              </Paper>
            </Box>
          </Slide>

          {/* Content area (scroll) */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0, // critical: allows the child to actually scroll
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              px: 1.25,
              pb: `calc(env(safe-area-inset-bottom) + 10px)`,
            }}
          >
                {mode === "search" ? (
                  <Paper
                    elevation={0}
                    sx={{
                      bgcolor: "#000000",
                      borderRadius: "14px",
                      border: "1px solid rgba(255,255,255,0.10)",
                      overflow: "hidden",
                    }}
                  >
                    {grouped.length === 0 && !loading ? (
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          Type at least 2 characters to search.
                        </Typography>
                      </Box>
                    ) : null}

                    {grouped.map(([groupName, items]) => (
                      <Box key={groupName}>
                        <Box
                          sx={{
                            px: 1.25,
                            py: 0.9,
                            bgcolor: "rgba(255,255,255,0.08)",
                            borderBottom: "1px solid rgba(255,255,255,0.08)",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              opacity: 1,
                              color: "#ffffff",
                              fontWeight: 900,
                              letterSpacing: "0.08em",
                              textTransform: "uppercase",
                            }}
                          >
                            {groupName}
                          </Typography>
                        </Box>
                        <List dense sx={{ py: 0 }}>
                          {items.slice(0, 12).map((r) => (
                            (() => {
                              const a = typeAccent(r.type);
                              return (
                            <ListItemButton
                              key={r.key}
                              onClick={() => navigateTo(r.route)}
                              sx={{
                                py: 1,
                                px: 1.25,
                                borderBottom: "1px solid rgba(255,255,255,0.06)",
                                "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
                              }}
                            >
                              <Icon sx={{ mr: 1.0, color: "#ffffff" }}>{a.icon}</Icon>
                              <ListItemText
                                primary={r.label}
                                secondary={r.subtitle || ""}
                                primaryTypographyProps={{ variant: "body2", sx: { color: "#ffffff", fontWeight: 800 } }}
                                secondaryTypographyProps={{ variant: "caption", sx: { color: "#ffffff" } }}
                              />
                              <Icon fontSize="small" sx={{ color: "#ffffff" }}>
                                chevron_right
                              </Icon>
                            </ListItemButton>
                              );
                            })()
                          ))}
                        </List>
                      </Box>
                    ))}
                  </Paper>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
                    {/* Messages scroll independently */}
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", pr: 0.25, pb: 10 }}>
                      <Stack spacing={0.75} sx={{ px: 0.25, pb: 1 }}>
                        {chatMessages.map((m, idx) => {
                          const isUser = m.role === "user";
                          return (
                            <Stack key={idx} direction="row" justifyContent={isUser ? "flex-end" : "flex-start"}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 1.1,
                                  maxWidth: "92%",
                                  borderRadius: "14px",
                                  borderColor: "rgba(255,255,255,0.10)",
                                  bgcolor: isUser ? "rgba(56,189,248,0.20)" : "rgba(255,255,255,0.08)",
                                  color: "#ffffff",
                                }}
                              >
                                {renderMarkdownLite(m.content)}
                              </Paper>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Box>

                    {/* Reply bar pinned at bottom */}
                    <Box
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        px: 0.25,
                        pt: 1,
                        pb: `calc(env(safe-area-inset-bottom) + 10px)`,
                        bgcolor: "#000000",
                        borderTop: "1px solid rgba(255,255,255,0.10)",
                      }}
                    >
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Reply…"
                          value={replyValue}
                          onChange={(e) => setReplyValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              sendReply();
                            }
                          }}
                          disabled={sendingReply}
                          sx={fieldSx}
                          InputProps={{ sx: { borderRadius: "12px" } }}
                        />
                        <IconButton
                          aria-label="Send reply"
                          onClick={sendReply}
                          disabled={sendingReply || !replyValue.trim()}
                          sx={{
                            color: "#ffffff",
                            borderRadius: "12px",
                            bgcolor: "rgba(255,255,255,0.08)",
                            border: "1px solid rgba(255,255,255,0.10)",
                            "&:hover": { bgcolor: "rgba(255,255,255,0.10)" },
                          }}
                        >
                          <Icon>send</Icon>
                        </IconButton>
                      </Stack>
                    </Box>
                  </Box>
                )}
          </Box>
          </Box>
      </Backdrop>
    </Fade>
  );
}

MobileSearchOverlay.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
};
